import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift, Sparkles, Heart, Mail, Check, Leaf, RefreshCw, Droplet, ExternalLink, ArrowLeft, Quote, Star, ShoppingBag, ChevronDown } from 'lucide-react';
import { useCgvGate } from '../components/CgvGate';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_AMOUNTS = [15, 30, 50, 100];

// 🔐 Liens Square (à brancher par Hayat depuis le dashboard Square)
// Page Square officielle pour l'achat de cartes cadeaux (e-gift cards trackées par Square)
// Source : Square Dashboard → Gift Cards → Order Page
const SQUARE_GIFT_CARD_URL = 'https://app.squareup.com/gift/MLAT0T7D6031F/order';
const SQUARE_LINKS = {
  15: SQUARE_GIFT_CARD_URL,
  30: SQUARE_GIFT_CARD_URL,
  50: SQUARE_GIFT_CARD_URL,
  100: SQUARE_GIFT_CARD_URL,
  custom: SQUARE_GIFT_CARD_URL,
};

// PayPal URL builder (Buy Now button avec email)
const PAYPAL_EMAIL = process.env.REACT_APP_PAYPAL_EMAIL || '';
const buildPaypalUrl = (itemName, amount) => {
  if (!PAYPAL_EMAIL) return '';
  const params = new URLSearchParams({
    cmd: '_xclick',
    business: PAYPAL_EMAIL,
    item_name: itemName,
    amount: amount.toFixed(2),
    currency_code: 'EUR',
    no_shipping: '2',
    no_note: '1',
    rm: '1',
  });
  return `https://www.paypal.com/cgi-bin/webscr?${params.toString()}`;
};

const openPaypal = (itemName, amount) => {
  const url = buildPaypalUrl(itemName, amount);
  if (!url) {
    toast.info('Paiement PayPal en cours de configuration. Réservez par téléphone : 04 67 21 57 08');
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

// Bandeau Témoignages clients pour la section Rituels (sur le concept signature olfactive)
const RituelsTestimonials = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/testimonials`)
      .then(({ data }) => { if (mounted) setItems((data || []).filter(t => t.approved !== false).slice(0, 3)); })
      .catch(() => { if (mounted) setItems([]); });
    return () => { mounted = false; };
  }, []);
  if (!items.length) return null;
  return (
    <div className="mt-16 max-w-6xl mx-auto" data-testid="rituels-testimonials">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">Elles &amp; ils ont vécu l&apos;expérience</p>
        <h3
          className="text-3xl md:text-4xl font-light text-[#5C2018]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
        >
          Ce qu&apos;ils ont découvert
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((t) => (
          <div
            key={t.id}
            className="relative bg-white rounded-2xl p-6 border border-[#B8922A]/30 shadow-[0_15px_40px_rgba(92, 32, 24,0.06)] hover:shadow-[0_20px_50px_rgba(92, 32, 24,0.12)] transition-shadow"
            data-testid={`testimonial-${t.id}`}
          >
            <Quote size={28} className="text-[#B8922A]/40 absolute top-4 right-4" />
            <div className="flex gap-0.5 mb-3" aria-label={`${t.rating} étoiles sur 5`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  className={i < (t.rating || 5) ? 'fill-[#B8922A] text-[#B8922A]' : 'text-[#B8922A]/20'}
                />
              ))}
            </div>
            <p className="text-sm text-[#332724] font-light leading-relaxed italic mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              « {t.comment} »
            </p>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C2018] font-medium">
              — {t.name}
            </p>
          </div>
        ))}
      </div>
      <div className="text-center mt-8">
        <Link
          to="/avis"
          className="inline-flex items-center gap-2 text-sm text-[#1E7A6A] hover:text-[#5C2018] underline underline-offset-4 transition-colors"
          data-testid="rituels-testimonials-link"
        >
          Lire tous les témoignages
          <ArrowLeft size={14} className="rotate-180" />
        </Link>
      </div>
    </div>
  );
};


// Flacon SVG dont la taille varie selon le volume (10 ml → 100 ml)
const FlaconIcon = ({ color, ml }) => {
  // Extrait le nombre depuis "30 ml", "100ml", "10 ml"
  const num = parseInt(String(ml || '30').replace(/\D/g, ''), 10) || 30;
  // Hauteur scalée : 10ml → 32px, 30ml → 38px, 50ml → 42px, 100ml → 50px
  const height = Math.round(28 + Math.min(num, 100) * 0.22);
  const width = Math.round(height * 0.55);
  return (
    <div className="flex-shrink-0 flex items-end justify-center" style={{ width: 48, height: 52 }}>
      <svg width={width} height={height} viewBox="0 0 22 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Pulvérisateur (cap) */}
        <rect x="7" y="0" width="8" height="3.5" rx="0.8" fill={color} />
        <rect x="8.5" y="3.5" width="5" height="1.8" fill={color} opacity="0.9" />
        {/* Col */}
        <rect x="9" y="5.3" width="4" height="3" fill={color} opacity="0.7" />
        {/* Corps du flacon */}
        <rect
          x="2"
          y="8.3"
          width="18"
          height="30"
          rx="2.5"
          fill={color}
          fillOpacity="0.78"
          stroke={color}
          strokeWidth="1.2"
        />
        {/* Reflet */}
        <ellipse cx="6.5" cy="15" rx="1.5" ry="4" fill="white" fillOpacity="0.32" />
        {/* Étiquette */}
        <rect x="5" y="22" width="12" height="8" rx="0.6" fill="white" fillOpacity="0.18" stroke="white" strokeOpacity="0.25" strokeWidth="0.5" />
      </svg>
    </div>
  );
};

// Ligne d'achat cliquable (Rituel × Gamme) — Ajout au panier + description dépliable
const BuyRow = ({ sku, label, desc, price, color = '#5C2018', variant, disabled, testid }) => {
  const isDark = variant === 'dark';
  const isUnavailable = typeof price === 'string' && price.includes('Sur');
  const numericPrice = typeof price === 'number' ? price : parseFloat(String(price).replace('€', '').replace(',', '.'));
  const { add: addToCart } = useCart();
  const [open, setOpen] = useState(false);

  const handleAdd = (e) => {
    e.stopPropagation();
    if (disabled || isUnavailable) return;
    addToCart({ sku, label: `Harmony · ${label}`, unit_price: numericPrice });
    toast.success(`Ajouté au panier · ${label}`, {
      action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
    });
  };

  const baseBg = isDark ? 'rgba(253,250,244,0.08)' : '#FDFAF4';
  const baseText = isDark ? '#FDFAF4' : color;
  const baseBorder = isDark ? 'rgba(253,250,244,0.2)' : 'rgba(184,146,42,0.25)';
  const descBg = isDark ? 'rgba(253,250,244,0.06)' : 'rgba(255,255,255,0.7)';
  const descText = isDark ? 'rgba(253,250,244,0.92)' : '#332724';
  const iconColor = isDark ? '#B8922A' : color;

  return (
    <li>
      <div
        data-testid={testid}
        className={`flex flex-col gap-2 px-3 py-2.5 rounded-lg border transition-all ${disabled || isUnavailable ? 'opacity-60' : ''}`}
        style={{ backgroundColor: baseBg, color: baseText, borderColor: baseBorder }}
      >
        <div className="flex items-center gap-3">
          <FlaconIcon color={iconColor} ml={label} />
          <button
            type="button"
            onClick={() => desc && !isUnavailable && setOpen((o) => !o)}
            className="flex-1 text-left flex items-center gap-1.5 min-w-[120px] hover:underline underline-offset-2 decoration-dotted cursor-pointer"
            style={{ color: baseText }}
            data-testid={`${testid}-name`}
            disabled={!desc || isUnavailable}
          >
            <span className="font-medium">{label}</span>
            {desc && !isUnavailable && (
              <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} style={{ opacity: 0.6 }} />
            )}
          </button>
          {isUnavailable ? (
            <em className="text-xs" style={{ color: isDark ? 'rgba(253,250,244,0.6)' : 'rgba(51,39,36,0.5)' }}>{price}</em>
          ) : (
            <strong style={{ color: baseText }}>{price}</strong>
          )}
          {!disabled && !isUnavailable && (
            <button
              type="button"
              onClick={handleAdd}
              className="text-[10px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-md transition-colors flex items-center gap-1 hover:opacity-90 flex-shrink-0"
              style={{ backgroundColor: isDark ? '#B8922A' : color, color: '#FDFAF4' }}
              data-testid={`${testid}-add`}
              title="Ajouter au panier"
            >
              <ShoppingBag size={12} /> Ajouter
            </button>
          )}
        </div>
        {open && desc && (
          <div
            className="px-3 py-2.5 rounded-md text-sm leading-relaxed animate-fadeIn"
            style={{ backgroundColor: descBg, color: descText, borderLeft: `3px solid ${iconColor}` }}
            data-testid={`${testid}-desc`}
          >
            {desc}
          </div>
        )}
      </div>
    </li>
  );
};

// Ligne recharge cliquable (gamme × format) — Ajout au panier + description dépliable
const RechargeRow = ({ sku, gamme, desc, ml, price, color, hoverColor, testid }) => {
  const { add: addToCart } = useCart();
  const [open, setOpen] = useState(false);
  const label = `Recharge ${gamme} · ${ml}`;
  const handleAdd = () => {
    addToCart({ sku, label: `Harmony · ${label}`, unit_price: price });
    toast.success(`Ajouté au panier · ${label}`, {
      action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
    });
  };
  return (
    <li className="flex flex-col gap-2 p-2 rounded-lg" data-testid={testid} style={{ backgroundColor: 'rgba(253,250,244,0.6)' }}>
      <div className="flex items-center gap-3">
        <FlaconIcon color={color} ml={ml} />
        <button
          type="button"
          onClick={() => desc && setOpen((o) => !o)}
          className="text-sm text-[#2E120D] flex-1 min-w-[100px] text-left flex items-center gap-1.5 hover:underline underline-offset-2 decoration-dotted cursor-pointer font-medium"
          data-testid={`${testid}-name`}
        >
          Recharge {ml}
          {desc && (
            <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} style={{ opacity: 0.6 }} />
          )}
        </button>
        <div className="flex items-center gap-2">
          <strong style={{ color }}>{price}€</strong>
          <button
            type="button"
            onClick={handleAdd}
            className="text-[#FDFAF4] px-3 py-1.5 rounded-md text-[10px] uppercase tracking-[0.1em] transition-colors flex items-center gap-1 flex-shrink-0"
            style={{ backgroundColor: color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverColor)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = color)}
            data-testid={`${testid}-add`}
          >
            <ShoppingBag size={12} /> Ajouter
          </button>
        </div>
      </div>
      {open && desc && (
        <div
          className="px-3 py-2.5 rounded-md text-sm leading-relaxed text-[#332724] animate-fadeIn"
          style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderLeft: `3px solid ${color}` }}
          data-testid={`${testid}-desc`}
        >
          {desc}
        </div>
      )}
    </li>
  );
};

const BoutiquePage = () => {
  const { slug } = useParams();
  const showSection = (s) => !slug || slug === s;
  const isFiltered = Boolean(slug);
  const { requireAcceptance } = useCgvGate();
  const { add: addToCart } = useCart();
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [form, setForm] = useState({
    buyer_name: '',
    buyer_email: '',
    recipient_name: '',
    recipient_email: '',
    message: '',
    send_to_recipient: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Fragrance catalogue
  const [perfumes, setPerfumes] = useState([]);
  const [loadingPerfumes, setLoadingPerfumes] = useState(true);
  const [selectedCollection, setSelectedCollection] = useState('all');

  const collections = [
    { id: 'all', name: 'Toutes', color: '#2E120D' },
    { id: 'iconiques', name: 'Iconiques', color: '#5C2018' },
    { id: 'pepites', name: 'Pépites', color: '#1E7A6A' },
    { id: 'audacieux', name: 'Audacieux', color: '#2E120D' },
  ];

  useEffect(() => {
    const fetchPerfumes = async () => {
      try {
        setLoadingPerfumes(true);
        const url = selectedCollection === 'all' ? `${API}/perfumes` : `${API}/perfumes?collection=${selectedCollection}`;
        const { data } = await axios.get(url);
        setPerfumes(data);
      } catch (err) {
        console.error('Error fetching perfumes:', err);
      } finally {
        setLoadingPerfumes(false);
      }
    };
    fetchPerfumes();
  }, [selectedCollection]);

  // Scroll to hash anchor on mount (handles /boutique#abonnement, /boutique#fragrances...)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      // small delay to let the DOM render
      setTimeout(tryScroll, 300);
    }
  }, []);

  const formatPrice = (prices) => {
    if (typeof prices === 'object' && prices !== null) {
      return Object.entries(prices)
        .map(([size, price]) => `${size} · ${price}€`)
        .join('  ·  ');
    }
    return '';
  };

  const getAmount = () => {
    if (useCustom) {
      const v = parseFloat(customAmount);
      return isNaN(v) ? 0 : v;
    }
    return selectedAmount;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = getAmount();
    if (amount < 10 || amount > 500) {
      toast.error('Le montant doit être compris entre 10€ et 500€');
      return;
    }
    if (!form.buyer_name || !form.buyer_email) {
      toast.error('Veuillez renseigner votre nom et votre email');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { amount, ...form };
      const { data } = await axios.post(`${API}/gift-cards`, payload);
      setConfirmation(data);
      toast.success('Votre demande a bien été enregistrée !');
    } catch (err) {
      toast.error('Une erreur est survenue. Merci de réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmation screen (après soumission bon cadeau)
  if (confirmation) {
    const amount = confirmation.amount;
    const squareLink = useCustom ? SQUARE_LINKS.custom : SQUARE_LINKS[amount];
    return (
      <div className="min-h-screen bg-[#FDFAF4] py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-[#1E7A6A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="text-[#1E7A6A]" size={36} />
          </div>
          <h1
            className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Merci pour votre commande !
          </h1>
          <p className="text-lg text-[#332724] font-light mb-8 leading-relaxed">
            Votre bon cadeau de <strong>{amount}€</strong> a bien été enregistré. <br />
            Pour le valider, merci de procéder au paiement sécurisé CB ci-dessous.
          </p>
          <div className="bg-white border border-gold p-6 rounded-2xl mb-8 inline-block">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">Référence</p>
            <p className="text-2xl text-[#5C2018] font-mono">{confirmation.code}</p>
            <p className="text-xs text-[#332724]/70 mt-2">À conserver pour toute correspondance</p>
          </div>
          {squareLink ? (
            <button
              type="button"
              onClick={() => requireAcceptance(
                `Harmony · Bon cadeau ${amount}€ (réf. ${confirmation.code})`,
                () => window.open(squareLink, '_blank', 'noopener,noreferrer')
              )}
              className="inline-block btn-primary px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em]"
              data-testid="square-payment-link"
            >
              Procéder au paiement CB
            </button>
          ) : (
            <div className="bg-[#FDFAF4] border border-gold p-6 rounded-2xl text-left">
              <p className="text-[#332724] font-light leading-relaxed">
                Nous vous contactons sous 24h avec votre lien de paiement Square personnalisé.
                <br /><br />
                <strong>Vérifiez votre email</strong> ({confirmation.buyer_email}) et n'hésitez pas
                à nous joindre au <a href="tel:0467215708" className="text-[#1E7A6A]">04 67 21 57 08</a> si besoin.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const quickNav = [
    { id: 'rituels', icon: <Sparkles size={20} />, title: 'Rituels Parfum', sub: 'Flacon + consultation' },
    { id: 'abonnement', icon: <RefreshCw size={20} />, title: 'Abonnement', sub: 'Flacon nomade mensuel' },
    { id: 'recharges', icon: <Leaf size={20} />, title: 'Recharges', sub: 'Geste éco-responsable' },
    { id: 'echantillons', icon: <Droplet size={20} />, title: 'Échantillons 2 ml', sub: 'Découvrir à 2€' },
    { id: 'bons-cadeaux', icon: <Gift size={20} />, title: 'Bons cadeaux', sub: 'Offrir un moment' },
    { id: 'fragrances', icon: <Heart size={20} />, title: 'Nos Fragrances', sub: 'Explorer le catalogue' },
    { id: 'coffret', icon: <Gift size={20} />, title: 'Coffret cadeau', sub: 'L\'écrin sur-mesure · +5€' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-20">
      {/* HERO (caché en mode produit) */}
      {!isFiltered && (
      <section className="relative py-20 px-6 bg-[#FDFAF4] border-b border-[#B8922A]/20 overflow-hidden" data-testid="boutique-hero">
        <img
          src="/assets/rose-filigrane.jpg"
          alt=""
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] object-contain pointer-events-none select-none mix-blend-multiply opacity-[0.07]"
        />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">La boutique Harmony</p>
          <h1
            className="text-5xl md:text-6xl font-light mb-6 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="boutique-title"
          >
            Tout notre univers, en ligne
          </h1>
          <p className="text-lg text-[#332724] font-light leading-relaxed max-w-2xl mx-auto">
            Rituels Parfum, recharges éco-responsables, bons cadeaux, coaching.
            <br />
            <strong>Vous payez l'expérience, vous repartez avec votre signature.</strong>
          </p>
        </div>
      </section>
      )}

      {/* Breadcrumb retour boutique (mode produit uniquement) */}
      {isFiltered && (
        <section className="py-6 px-6 bg-[#FDFAF4] border-b border-[#B8922A]/15">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <Link to="/boutique" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B8922A] hover:text-[#5C2018] transition-colors" data-testid="back-to-boutique">
              <ArrowLeft size={14} />
              Retour à la boutique
            </Link>
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#B8922A]/70 italic">
              📸 Photos non contractuelles
            </p>
          </div>
        </section>
      )}

      {/* QUICK NAV - 6 cartes ancres (caché en mode produit) */}
      {!isFiltered && (
      <section className="py-10 px-6 bg-white border-b border-[#B8922A]/10" data-testid="boutique-quick-nav">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickNav.map((q) => (
            <a
              key={q.id}
              href={`#${q.id}`}
              className="group bg-[#FDFAF4] hover:bg-[#5C2018] hover:text-[#FDFAF4] border border-gold rounded-2xl p-5 text-center transition-all"
              data-testid={`nav-${q.id}`}
            >
              <div className="w-12 h-12 bg-[#5C2018]/10 group-hover:bg-[#B8922A]/30 rounded-full flex items-center justify-center mx-auto mb-3 text-[#5C2018] group-hover:text-[#FDFAF4] transition-colors">
                {q.icon}
              </div>
              <p className="text-sm uppercase tracking-[0.15em] text-[#5C2018] group-hover:text-[#FDFAF4] mb-1">{q.title}</p>
              <p className="text-xs text-[#332724]/70 group-hover:text-[#FDFAF4]/80">{q.sub}</p>
            </a>
          ))}
        </div>
      </section>
      )}

      {/* DÉMARCHE - manifeste (caché en mode produit) */}
      {!isFiltered && (
      <section className="py-14 px-6 bg-[#FDFAF4]" data-testid="boutique-demarche">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Notre démarche</p>
          <h2
            className="text-3xl md:text-4xl text-[#5C2018] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Le luxe du temps, l'art de la rencontre
          </h2>
          <p className="text-base text-[#B8922A] italic mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Une écoute, une signature.
          </p>
          <p className="text-base md:text-lg text-[#332724] font-light leading-relaxed">
            Chez Harmony, vous n'achetez pas un produit dans un panier. Vous réservez un moment :
            une <strong>consultation olfactive 15 min offerte</strong> avec votre Rituel, une remise
            en mains propres pour vos recharges, un <strong>rendez-vous personnalisé</strong> derrière
            chaque bon cadeau.
          </p>
        </div>
      </section>
      )}

      {/* ============ SECTION 1 : RITUELS PARFUM ============ */}
      {showSection('rituels') && (
      <section id="rituels" className="py-16 px-6 bg-white border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-rituels">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">💎 Un parfum choisi POUR vous</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Les Rituels Parfum
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-3xl mx-auto mb-4">
              <strong>Vous ne choisissez pas un parfum. Vous nous laissez révéler le vôtre.</strong>
            </p>
            <p className="text-sm text-[#332724]/85 font-light leading-relaxed max-w-3xl mx-auto mb-4">
              Le Rituel Harmony, c&apos;est l&apos;inverse d&apos;un achat classique :
              vous réservez d&apos;abord votre flacon (format et budget), puis nos
              experts vous guident lors d&apos;une <strong>consultation olfactive de 15 min
              offerte</strong> (en boutique au Polygone de Béziers ou en visio) pour
              identifier votre <strong>signature olfactive</strong> — vos notes, vos
              accords, votre histoire — et vous recommander la fragrance qui vous correspond
              vraiment, parmi nos trois gammes (Iconiques, Pépites, Audacieux).
            </p>
            <p className="text-sm text-[#1E7A6A] italic font-light leading-relaxed max-w-3xl mx-auto">
              💡 Le parfum vous est révélé pendant la consultation, puis vous l&apos;emportez
              avec vous (Click &amp; Collect) ou nous vous l&apos;expédions.
            </p>
          </div>

          {/* 3 étapes simples — Click & Collect */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 max-w-4xl mx-auto">
            {[
              { num: '1', title: 'Je réserve mon flacon', text: 'Format et budget en ligne, paiement CB sécurisé. Aucun parfum à choisir à ce stade.' },
              { num: '2', title: 'Je découvre MA fragrance', text: 'Consultation 15 min offerte en boutique ou en visio : nos experts identifient votre signature olfactive.' },
              { num: '3', title: 'J\'emporte mon parfum', text: 'Sur place (Click & Collect) ou par livraison Colissimo. Toujours le bon, parce que choisi pour vous.' },
            ].map((s) => (
              <div key={s.num} className="bg-[#FDFAF4] p-5 rounded-2xl border border-gold flex items-start gap-4">
                <div className="w-10 h-10 bg-[#5C2018] text-[#FDFAF4] rounded-full flex items-center justify-center flex-shrink-0 text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  {s.num}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#5C2018] mb-1">{s.title}</p>
                  <p className="text-sm text-[#332724] font-light leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* 2 Rituels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Rituel Découverte */}
            <div className="bg-white rounded-2xl border border-gold p-6 flex flex-col shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]" data-testid="rituel-decouverte">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2 text-center">Rituel Découverte</p>
              <h3 className="text-2xl text-[#5C2018] text-center mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Mon premier flacon
              </h3>
              <p className="text-xs text-[#332724]/60 text-center mb-5">+ consultation 15 min offerte</p>
              <ul className="space-y-2 text-sm">
                <BuyRow sku="rit-dec-ico-30" label="Iconiques · 30 ml" desc="Élégance intemporelle — un classique qui traverse les saisons." price="17€" color="#5C2018" testid="buy-dec-iconiques" />
                <BuyRow sku="rit-dec-pep-10" label="Pépites · 10 ml" desc="Originalité confidentielle — une fragrance rare, qui ne s'achète pas partout." price="15€" color="#1E7A6A" testid="buy-dec-pepites" />
                <BuyRow sku="rit-dec-aud-10" label="Audacieux · 10 ml" desc="Caractère expressif — une signature qui marque, qui se remarque." price="25€" color="#2E120D" testid="buy-dec-audacieux" />
              </ul>
            </div>

            {/* Rituel Signature */}
            <div className="bg-[#5C2018] text-[#FDFAF4] rounded-2xl p-6 flex flex-col shadow-[0_15px_50px_rgba(92, 32, 24,0.25)] relative" data-testid="rituel-signature">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B8922A] text-[#FDFAF4] px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em]">
                Le plus choisi
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2 text-center mt-2">Rituel Signature</p>
              <h3 className="text-2xl text-[#FDFAF4] text-center mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Mon parfum au quotidien
              </h3>
              <p className="text-xs text-[#FDFAF4]/70 text-center mb-5">50 ou 100 ml · + consultation 15 min offerte</p>
              <ul className="space-y-2 text-sm">
                <BuyRow sku="rit-sig-ico-50" label="Iconiques · 50 ml" desc="Élégance intemporelle — votre signature classique au quotidien." price="25€" variant="dark" testid="buy-sig-iconiques" />
                <BuyRow sku="rit-sig-ico-100" label="Iconiques · 100 ml" desc="Format généreux pour les Iconiques que vous portez chaque jour." price="40€" variant="dark" testid="buy-sig-iconiques-100" />
                <BuyRow sku="rit-sig-pep-50" label="Pépites · 50 ml" desc="Une pépite rare adoptée comme parfum signature." price="39€" variant="dark" testid="buy-sig-pepites" />
                <BuyRow sku="rit-sig-aud-50" label="Audacieux · 50 ml" desc="Le caractère affirmé porté en grand format." price="59€" variant="dark" testid="buy-sig-audacieux" />
              </ul>
            </div>
          </div>

          {/* Options en supplément (Personnalisation + Coffret) */}
          <div className="mt-10 max-w-5xl mx-auto" data-testid="options-supplements">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-2">
                Options en supplément
              </p>
              <h3 className="text-2xl md:text-3xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Sublimez votre Rituel
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Personnalisation */}
              <div className="bg-gradient-to-br from-[#FDFAF4] to-white border-2 border-[#B8922A]/40 rounded-2xl p-6 flex flex-col" data-testid="personnalisation-encart">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 bg-[#B8922A]/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#B8922A] text-xl">✨</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline gap-2 mb-1">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#B8922A]">Personnalisation</p>
                      <span className="text-xl font-light text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                        +5€
                      </span>
                    </div>
                    <h4 className="text-lg text-[#5C2018] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                      Votre signature unique
                    </h4>
                    <p className="text-xs text-[#332724] font-light leading-relaxed mb-2">
                      Ajoutez une note personnelle à votre fragrance — un accord, un dosage, une signature qui n&apos;appartient qu&apos;à vous.
                    </p>
                    <p className="text-[11px] text-[#1E7A6A] font-light leading-relaxed">
                      💎 <strong>Payée une fois.</strong> Formule enregistrée à vie au fichier client.
                    </p>
                    <p className="text-[10px] text-[#332724]/70 italic mt-2">
                      À demander lors de votre consultation — ou pré-réglée en ligne ci-dessous.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#B8922A]/15">
                  <button
                    type="button"
                    onClick={() => {
                      addToCart({ sku: 'option-perso', label: 'Harmony · Personnalisation Rituel (formule unique)', unit_price: 5 });
                      toast.success('Personnalisation ajoutée au panier', {
                        action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
                      });
                    }}
                    className="flex-1 btn-primary px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.15em] flex items-center justify-center gap-1.5"
                    data-testid="perso-add-btn"
                  >
                    <ShoppingBag size={12} /> Ajouter · 5€
                  </button>
                </div>
              </div>

              {/* Coffret cadeau */}
              <div className="bg-gradient-to-br from-[#FDFAF4] to-white border-2 border-[#B8922A]/40 rounded-2xl p-6 flex flex-col" data-testid="coffret-encart-rituel">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 bg-[#B8922A]/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <Gift size={20} className="text-[#B8922A]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline gap-2 mb-1">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#B8922A]">Coffret cadeau</p>
                      <span className="text-xl font-light text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                        +5€
                      </span>
                    </div>
                    <h4 className="text-lg text-[#5C2018] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                      L&apos;écrin sur-mesure
                    </h4>
                    <p className="text-xs text-[#332724] font-light leading-relaxed mb-2">
                      Boîte cadeau bordeaux/doré, papier de soie crème, ruban satiné, étiquette personnalisée à la main + carte-message sur demande.
                    </p>
                    <p className="text-[10px] text-[#332724]/70 italic mt-2">
                      📸 Photo non contractuelle · à ajouter en supplément de votre Rituel.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#B8922A]/15">
                  <button
                    type="button"
                    onClick={() => {
                      addToCart({ sku: 'option-coffret', label: 'Harmony · Coffret cadeau (supplément écrin)', unit_price: 5 });
                      toast.success('Coffret cadeau ajouté au panier', {
                        action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
                      });
                    }}
                    className="flex-1 btn-primary px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.15em] flex items-center justify-center gap-1.5"
                    data-testid="coffret-rituel-add"
                  >
                    <ShoppingBag size={12} /> Ajouter · 5€
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Engagement & frais de port */}
          <div className="mt-10 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 bg-[#FDFAF4] border-l-4 border-[#1E7A6A] p-6 md:p-7 rounded-r-2xl">
              <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-3">Notre engagement</p>
              <p className="text-base text-[#5C2018] font-light leading-relaxed mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Chez Harmony, on s'adapte à vous.
              </p>
              <p className="text-sm text-[#332724] font-light leading-relaxed mb-2">
                Avec votre Rituel, une consultation 15 min vous est offerte : utilisez-la pour découvrir votre parfum signature, pour affiner avec nos experts, ou pas du tout — venez simplement récupérer votre flacon si c'est ce qui vous fait plaisir.
              </p>
              <p className="text-sm text-[#332724]/85 font-light leading-relaxed italic">
                Notre promesse : du temps, de l'écoute, l'expertise et la connaissance olfactive — toujours, à votre rythme.
              </p>
            </div>
            <div className="bg-white border border-gold rounded-2xl p-5 text-center flex flex-col justify-center">
              <p className="text-xl mb-1">📦</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#1E7A6A] mb-2">Livraison France</p>
              <p className="text-sm text-[#332724] font-light leading-relaxed">
                <strong className="text-[#5C2018]">8,90€</strong> Colissimo
                <br />
                <span className="text-xs text-[#1E7A6A] italic">Offerte dès 60€ d'achat</span>
              </p>
              <p className="text-[10px] text-[#332724]/60 mt-2">
                Ou retrait gratuit en boutique
              </p>
            </div>
          </div>

          {/* TÉMOIGNAGES — Bandeau confiance signature olfactive */}
          <RituelsTestimonials />
        </div>
      </section>
      )}

      {/* ============ SECTION 2 : ABONNEMENT ============ */}
      {showSection('abonnement') && (
      <section id="abonnement" className="py-16 px-6 bg-[#1E7A6A]/[0.03] border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-abonnement">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-3">Section 2 · 🌙 Mon flacon nomade</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              L'abonnement mensuel
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-3xl mx-auto">
              Chaque mois, recevez (ou retirez en boutique) votre <strong>flacon nomade rechargé de 10 ml</strong> :
              votre parfum signature, toujours prêt à vous accompagner. Flexible, éco-responsable, sans engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {[
              { title: 'Flacon nomade rechargé', text: 'Votre flacon de 10 ml, rempli chaque mois selon vos envies.' },
              { title: 'Sans engagement', text: 'Modifiez ou annulez à tout moment, en un clic.' },
              { title: 'Éco-responsable', text: 'Un seul flacon, mille recharges. Zéro déchet, geste durable.' },
            ].map((b, i) => (
              <div key={i} className="bg-white p-5 rounded-2xl border border-gold">
                <div className="w-10 h-10 bg-[#1E7A6A]/15 rounded-full flex items-center justify-center mb-3 text-[#1E7A6A]">
                  <RefreshCw size={18} />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">{b.title}</p>
                <p className="text-sm text-[#332724] font-light leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#1E7A6A] text-[#FDFAF4] rounded-2xl p-8 md:p-10 text-center" data-testid="abonnement-cta">
            <h3 className="text-3xl md:text-4xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              Je m'abonne
            </h3>
            <p className="text-base font-light mb-6 max-w-xl mx-auto opacity-90">
              Rejoignez la communauté Harmony et ne soyez plus jamais à court de votre signature.
            </p>
            <p className="text-3xl mb-5 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              10€<span className="text-sm opacity-80"> / mois</span>
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  addToCart({ sku: 'abonnement-10ml', label: 'Harmony · Abonnement mensuel · Flacon nomade 10 ml', unit_price: 10 });
                  toast.success('Abonnement ajouté au panier · 10€/mois', {
                    action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
                  });
                }}
                className="inline-flex items-center gap-2 bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-7 py-3 rounded-full text-xs uppercase tracking-[0.2em] transition-colors"
                data-testid="subscribe-add-btn"
              >
                <ShoppingBag size={14} /> Ajouter au panier · 10€/mois
              </button>
            </div>
            <p className="text-xs font-light mt-4 opacity-75">
              Sans engagement · Annulable à tout moment · 1<sup>er</sup> prélèvement le jour de votre 1<sup>ère</sup> retrait/livraison
            </p>
          </div>
        </div>
      </section>
      )}

      {/* ============ SECTION 3 : RECHARGES ============ */}
      {showSection('recharges') && (
      <section id="recharges" className="py-16 px-6 bg-[#FDFAF4] border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-recharges">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-3">Section 3 · ♻ Geste éco-responsable</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Vos recharges, en quelques clics
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-3xl mx-auto">
              Vous connaissez déjà votre parfum signature ? Commandez votre recharge en ligne et
              choisissez votre mode de récupération : <strong>retrait en boutique</strong>,
              <strong> remise en mains propres à Montpellier</strong> ou <strong>livraison France</strong>.
              <br />
              <span className="text-sm italic text-[#1E7A6A]">
                Envie d'explorer une nouvelle fragrance ? Une mini-consultation découverte est offerte sur demande.
              </span>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Iconiques */}
            <div className="bg-white rounded-2xl border border-[#5C2018]/40 p-6" data-testid="recharges-iconiques">
              <div className="text-center mb-5 pb-4 border-b border-[#5C2018]/20">
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C2018]/80 mb-1">Recharges</p>
                <h3 className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  Les Iconiques
                </h3>
              </div>
              <ul className="space-y-3">
                {[
                  { ml: '10ml', p: 9, desc: "Petit format à glisser dans le sac, idéal pour découvrir." },
                  { ml: '30ml', p: 15, desc: "Format pratique pour une utilisation quotidienne." },
                  { ml: '50ml', p: 23, desc: "Le format signature pour porter vos Iconiques au quotidien." },
                  { ml: '100ml', p: 37, desc: "Format généreux, le meilleur rapport qualité/prix." },
                ].map((r) => (
                  <RechargeRow key={r.ml} sku={`rech-ico-${r.ml}`} gamme="Iconiques" desc={r.desc} ml={r.ml} price={r.p} color="#5C2018" hoverColor="#481810" testid={`recharge-iconiques-${r.ml}`} />
                ))}
              </ul>
            </div>

            {/* Pépites */}
            <div className="bg-white rounded-2xl border border-[#1E7A6A]/40 p-6" data-testid="recharges-pepites">
              <div className="text-center mb-5 pb-4 border-b border-[#1E7A6A]/20">
                <p className="text-xs uppercase tracking-[0.2em] text-[#1E7A6A]/80 mb-1">Recharges</p>
                <h3 className="text-2xl text-[#1E7A6A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  Les Pépites
                </h3>
              </div>
              <ul className="space-y-3">
                {[
                  { ml: '10ml', p: 14, desc: "Petit format pour s'offrir une pépite rare et confidentielle." },
                  { ml: '50ml', p: 37, desc: "Votre pépite signature en format quotidien." },
                ].map((r) => (
                  <RechargeRow key={r.ml} sku={`rech-pep-${r.ml}`} gamme="Pépites" desc={r.desc} ml={r.ml} price={r.p} color="#1E7A6A" hoverColor="#155349" testid={`recharge-pepites-${r.ml}`} />
                ))}
              </ul>
            </div>

            {/* Audacieux */}
            <div className="bg-white rounded-2xl border border-[#2E120D]/40 p-6" data-testid="recharges-audacieux">
              <div className="text-center mb-5 pb-4 border-b border-[#2E120D]/20">
                <p className="text-xs uppercase tracking-[0.2em] text-[#2E120D]/80 mb-1">Recharges</p>
                <h3 className="text-2xl text-[#2E120D]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  Les Audacieux
                </h3>
              </div>
              <ul className="space-y-3">
                {[
                  { ml: '10ml', p: 24, desc: "Petit format pour oser un parfum au caractère affirmé." },
                  { ml: '50ml', p: 57, desc: "Le format signature pour porter votre Audacieux au quotidien." },
                ].map((r) => (
                  <RechargeRow key={r.ml} sku={`rech-aud-${r.ml}`} gamme="Audacieux" desc={r.desc} ml={r.ml} price={r.p} color="#2E120D" hoverColor="#1F0E08" testid={`recharge-audacieux-${r.ml}`} />
                ))}
              </ul>
            </div>
          </div>

          {/* Avantages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 text-center">
            <div className="bg-white p-4 rounded-xl border border-[#1E7A6A]/30">
              <p className="text-2xl mb-1">♻</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#1E7A6A] mb-1">Éco-responsable</p>
              <p className="text-xs text-[#332724] font-light">Réutilisez votre flacon, réduisez vos déchets</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-[#B8922A]/30">
              <p className="text-2xl mb-1">💰</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Économique</p>
              <p className="text-xs text-[#332724] font-light">Jusqu'à 15 % de réduction vs flacon neuf</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-[#5C2018]/30">
              <p className="text-2xl mb-1">📦</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C2018] mb-1">3 modes de retrait</p>
              <p className="text-xs text-[#332724] font-light">Béziers, Montpellier ou livraison France</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============ SECTION 4 : ÉCHANTILLONS ============ */}
      {showSection('echantillons') && (
      <section id="echantillons" className="py-16 px-6 bg-white border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-echantillons">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Section 4 · 🧪 Sentir avant d'aimer</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Les Échantillons 2 ml
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-2xl mx-auto">
              Curieux·se ? Découvrez nos fragrances à votre rythme : un petit format à <strong>2€</strong>,
              à porter quelques jours pour faire vraiment connaissance avec votre futur parfum signature.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { gamme: 'Les Iconiques', sku: 'ech-iconiques', desc: 'Fragrances intemporelles', color: '#5C2018' },
              { gamme: 'Les Pépites', sku: 'ech-pepites', desc: 'Rares & accessibles', color: '#1E7A6A' },
              { gamme: 'Les Audacieux', sku: 'ech-audacieux', desc: 'Avant-gardistes', color: '#2E120D' },
            ].map((s) => (
              <div key={s.gamme} className="bg-[#FDFAF4] rounded-2xl border border-gold p-6 text-center flex flex-col" data-testid={`echantillon-${s.gamme.toLowerCase().replace(/ /g, '-')}`}>
                <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center mb-3 border" style={{ borderColor: `${s.color}40` }}>
                  <Droplet size={20} style={{ color: s.color }} />
                </div>
                <h3 className="text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', color: s.color }}>
                  {s.gamme}
                </h3>
                <p className="text-xs text-[#332724]/85 mb-4 leading-relaxed px-2 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {s.gamme === 'Les Iconiques' && 'Fragrances intemporelles — pour découvrir notre gamme de classiques élégants avant de choisir votre signature.'}
                  {s.gamme === 'Les Pépites' && 'Fragrances rares & confidentielles — testez nos pépites uniques en miniature avant adoption.'}
                  {s.gamme === 'Les Audacieux' && 'Fragrances avant-gardistes — osez un parfum au caractère affirmé en mini-format.'}
                </p>
                <p className="text-2xl font-light mb-1" style={{ color: s.color, fontFamily: "'Cormorant Garamond', serif" }}>2€</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#332724]/60 mb-5">Échantillon 2 ml</p>
                <div className="flex justify-center mt-auto">
                  <button
                    type="button"
                    onClick={() => {
                      addToCart({ sku: s.sku, label: `Harmony · Échantillon ${s.gamme} 2 ml`, unit_price: 2 });
                      toast.success(`Échantillon ${s.gamme} ajouté au panier`, {
                        action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
                      });
                    }}
                    className="px-5 py-2 rounded-full text-[10px] uppercase tracking-[0.15em] transition-colors flex items-center gap-1.5 hover:opacity-90"
                    style={{ backgroundColor: s.color, color: '#FDFAF4' }}
                    data-testid={`ech-${s.sku}-add`}
                  >
                    <ShoppingBag size={12} /> Ajouter
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-[#332724]/60 mt-6 italic max-w-2xl mx-auto">
            💡 Astuce : l'échantillon est <strong>déductible du prix de votre Rituel</strong> si vous achetez le parfum complet ensuite. Demandez-nous lors de votre consultation.
          </p>
        </div>
      </section>
      )}

      {/* ============ SECTION 5 : BONS CADEAUX ============ */}
      {showSection('bons-cadeaux') && (
      <section id="bons-cadeaux" className="py-16 px-6 bg-[#FDFAF4] border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-bons-cadeaux">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Section 5 · 🎁 Offrir</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Offrez un moment Harmony
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-2xl mx-auto">
              Le bon cadeau Harmony, ce n'est pas un voucher anonyme. C'est offrir
              <strong> un rendez-vous, une écoute, une signature olfactive.</strong>
              <br />
              Votre proche réservera <em>sa</em> consultation (15 min) et repartira avec <em>son</em> parfum signature. Valable 1 an.
            </p>
          </div>

          {/* 3 piliers du bon cadeau */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { num: '01', title: 'Une écoute', text: '15 min de consultation olfactive personnalisée avec nos experts. Souvenirs, émotions, style de vie.' },
              { num: '02', title: 'Une expertise', text: 'Plus de 100 fragrances explorées — Iconiques, Pépites, Audacieux — pour trouver CELLE qui correspond.' },
              { num: '03', title: 'Une signature', text: 'Le bon couvre le format choisi (10, 30, 50 ou 100 ml). Sur-mesure possible en supplément.' },
            ].map((s) => (
              <div key={s.num} className="bg-[#FDFAF4] p-6 rounded-2xl border border-gold">
                <p className="text-3xl text-[#B8922A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                  {s.num}
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C2018] mb-2">{s.title}</p>
                <p className="text-sm text-[#332724] font-light leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>

          {/* CTA direct vers la page Square Gift Card officielle */}
          <div className="bg-[#FDFAF4] p-8 md:p-12 rounded-2xl border border-gold max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-[#5C2018]/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <Gift size={28} className="text-[#5C2018]" />
            </div>
            <h3 className="text-2xl md:text-3xl text-[#5C2018] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              Carte cadeau Maison Harmony
            </h3>
            <p className="text-sm md:text-base text-[#332724] font-light leading-relaxed mb-6">
              Choisissez le montant souhaité (de 10 à 500 €), personnalisez votre message et envoyez la carte
              <strong> instantanément par email</strong> au bénéficiaire — ou imprimez-la pour l'offrir en main propre.
              <br />
              <span className="text-xs italic text-[#332724]/70">
                Carte gérée et trackée directement par Square : suivi de solde automatique, valable 1 an, utilisable
                en boutique et en ligne.
              </span>
            </p>
            <a
              href="https://app.squareup.com/gift/MLAT0T7D6031F/order"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => requireAcceptance(
                'Harmony · Carte cadeau Square',
                () => window.open('https://app.squareup.com/gift/MLAT0T7D6031F/order', '_blank', 'noopener,noreferrer')
              )}
              className="inline-flex items-center gap-2 btn-primary px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] hover:opacity-90"
              data-testid="square-gift-card-cta"
            >
              <Gift size={16} /> Acheter une carte cadeau
            </a>
            <p className="text-xs text-[#332724]/60 mt-4">
              💳 Paiement CB sécurisé sur Square · Aucune donnée bancaire stockée chez nous
            </p>
          </div>

          {/* Parcours du bon cadeau */}
          <div className="mt-12 max-w-5xl mx-auto">
            <h4 className="text-xl text-[#5C2018] text-center mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              De votre commande à sa signature
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { icon: <Gift size={20} />, title: 'Vous commandez', text: 'Montant + message personnel. Paiement CB sécurisé.' },
                { icon: <Mail size={20} />, title: 'Le bon part', text: 'Par email à vous ou directement au bénéficiaire.' },
                { icon: <Sparkles size={20} />, title: 'Il/elle réserve', text: 'Sa consultation (boutique ou visio).' },
                { icon: <Heart size={20} />, title: 'Sa signature', text: 'Il/elle repart avec SON parfum, choisi avec nos experts.' },
              ].map((s, i) => (
                <div key={i} className="bg-[#FDFAF4] p-5 rounded-2xl border border-gold text-center">
                  <div className="w-10 h-10 bg-[#5C2018]/10 rounded-full flex items-center justify-center mx-auto mb-3 text-[#5C2018]">
                    {s.icon}
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">{s.title}</p>
                  <p className="text-sm text-[#332724] font-light leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ============ SECTION 6 : NOS FRAGRANCES (catalogue) ============ */}
      {showSection('fragrances') && (
      <section id="fragrances" className="py-16 px-6 bg-white border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-fragrances">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">Section 6 · 🌸 Le catalogue</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Nos Fragrances
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-3xl mx-auto">
              Explorez l'univers olfactif Harmony — plus de fragrances rechargeables, réparties en 3 collections.
              <br />
              Une envie en particulier ? Filtrez par gamme.
            </p>
          </div>

          {/* Visuels 3 gammes + bar parfum */}
          <div className="mb-10 max-w-5xl mx-auto">
            <img
              src="/assets/bar-parfum-vitrine-blurred.jpg"
              alt="Bar à Parfum Maison Harmony — à partir de 10€"
              className="w-full rounded-2xl shadow-lg mb-5"
            />
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#B8922A]/70 italic text-center -mt-3 mb-4">
              📸 Photo non contractuelle · noms de fragrances volontairement floutés
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <img src="/assets/gallery/gamme_iconiques.jpg" alt="Les Iconiques" className="w-full aspect-square object-contain rounded-2xl shadow-md bg-white" />
              <img src="/assets/gallery/gamme_pepites.jpg" alt="Les Pépites" className="w-full aspect-square object-contain rounded-2xl shadow-md bg-white" />
              <img src="/assets/gallery/gamme_audacieux.jpg" alt="Les Audacieux" className="w-full aspect-square object-contain rounded-2xl shadow-md bg-white" />
            </div>
          </div>

          {/* Filtre collections */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {collections.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedCollection(c.id)}
                className={`px-5 py-2.5 rounded-full border text-xs uppercase tracking-[0.2em] transition-all ${
                  selectedCollection === c.id
                    ? 'bg-[#5C2018] text-[#FDFAF4] border-[#5C2018]'
                    : 'bg-white text-[#332724] border-[#B8922A]/30 hover:border-[#5C2018]'
                }`}
                data-testid={`filter-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {/* Liste parfums */}
          {loadingPerfumes ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1E7A6A] mx-auto"></div>
              <p className="mt-3 text-sm text-[#332724]/70">Chargement des fragrances…</p>
            </div>
          ) : perfumes.length === 0 ? (
            <div className="bg-[#FDFAF4] rounded-2xl border border-gold p-8 md:p-12 text-center max-w-3xl mx-auto" data-testid="fragrances-empty">
              <p className="text-3xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>🌹</p>
              <h3 className="text-2xl text-[#5C2018] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Les fragrances se découvrent en boutique
              </h3>
              <p className="text-base text-[#332724] font-light leading-relaxed mb-4">
                Chez Harmony, les parfums se sentent, se vivent, se choisissent <em>avec</em> vous. Plus de fragrances à découvrir au cœur de nos 3 collections — <strong>Iconiques · Pépites · Audacieux</strong>.
              </p>
              <p className="text-sm text-[#332724]/80 font-light italic mb-6">
                Réservez votre consultation olfactive (gratuite, 15 min) ou venez nous rencontrer au Polygone de Béziers.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/boutique/rituels" className="btn-primary px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.2em]" data-testid="empty-cta-rituels">
                  Voir les Rituels
                </Link>
                <a href="/rendez-vous" className="px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] border border-[#5C2018] text-[#5C2018] hover:bg-[#5C2018] hover:text-[#FDFAF4] transition-colors" data-testid="empty-cta-rdv">
                  Prendre rendez-vous
                </a>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {perfumes.map((p) => (
                <div key={p.id} className="bg-[#FDFAF4] rounded-2xl border border-gold p-5 shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]" data-testid={`perfume-card-${p.id}`}>
                  {p.image_url && (
                    <div className="h-44 mb-4 overflow-hidden rounded-lg">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#B8922A] mb-1">{p.collection}</p>
                  <h3 className="text-xl text-[#5C2018] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                    {p.name}
                  </h3>
                  <p className="text-sm text-[#332724] font-light leading-relaxed mb-3">{p.description}</p>
                  <p className="text-xs text-[#332724]/70 mb-3 italic">Notes : {p.notes}</p>
                  <p className="text-xs font-medium text-[#1E7A6A] mb-3">{formatPrice(p.prices)}</p>
                  {p.refillable && (
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#1E7A6A]">♻ Rechargeable</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* CTA retour Rituels (si liste non vide) */}
          {perfumes.length > 0 && (
            <div className="text-center mt-12">
              <p className="text-sm text-[#332724]/80 italic mb-4">
                Un coup de cœur ? Choisissez votre Rituel pour commander votre flacon.
              </p>
              <Link to="/boutique/rituels" className="inline-block btn-primary px-8 py-3 rounded-full text-xs uppercase tracking-[0.2em]" data-testid="back-to-rituels">
                Voir les Rituels
              </Link>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ============ SECTION 7 : COACHING (teaser) ============ */}
      {!isFiltered && (
      <section id="coaching" className="py-14 px-6 bg-[#FDFAF4] border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-coaching">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-3">Section 7 · 🌸 Accompagnement</p>
          <h2
            className="text-3xl md:text-4xl font-light mb-5 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Le coaching Harmony
          </h2>
          <p className="text-base text-[#332724] font-light leading-relaxed max-w-2xl mx-auto mb-3">
            Au-delà du parfum, Hayat vous accompagne en <strong>gestion du stress</strong>, des
            <strong> émotions</strong>, en <strong>communication non violente</strong> et sur la
            <em> femme, mère, entrepreneure</em>.
          </p>
          <p className="text-sm text-[#332724]/80 italic mb-6">
            Inscrivez-vous à la liste d'attente pour être prévenu·e en priorité du lancement.
          </p>
          <a href="/coaching" className="inline-block btn-primary px-7 py-2.5 rounded-full text-xs uppercase tracking-[0.2em]" data-testid="goto-coaching-btn">
            Découvrir le coaching
          </a>
        </div>
      </section>
      )}

      {/* ============ SECTION 8 : VAPORISATEUR DE SAC ============ */}
      {showSection('vaporisateur') && (
      <section id="vaporisateur" className="py-16 px-6 bg-white border-t border-[#B8922A]/10 scroll-mt-20" data-testid="section-vaporisateur">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-3">💨 Accessoire</p>
            <h2
              className="text-4xl md:text-5xl font-light mb-5 text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Le vaporisateur de sac
            </h2>
            <p className="text-base text-[#332724] font-light leading-relaxed max-w-2xl mx-auto">
              Emportez votre fragrance partout avec vous. Rechargeable, élégant, disponible en <strong>plusieurs coloris</strong>.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden mb-8 shadow-[0_10px_40px_rgba(92, 32, 24,0.12)] max-w-3xl mx-auto">
            <img
              src="/assets/ai-vaporisateurs-hero.jpg"
              alt="Vaporisateurs de sac Maison Harmony — plusieurs coloris"
              className="w-full h-auto object-cover"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { ml: '5 ml', sku: 'vapo-5ml', price: 5, testid: 'vapo-5', img: '/assets/vapo-5ml-hayat.jpg', desc: "Le mini-flacon métallique format poche — glissé dans le sac, prêt à être ressorti pour une retouche en journée." },
              { ml: '12 ml', sku: 'vapo-12ml', price: 15, testid: 'vapo-12', img: '/assets/vapo-12ml-hayat.jpg', desc: "Le format voyage idéal — plus généreux, parfait pour les week-ends et déplacements professionnels." },
            ].map((v) => (
              <div key={v.ml} className="bg-[#FDFAF4] rounded-2xl border border-gold overflow-hidden flex flex-col" data-testid={v.testid}>
                <div className="h-56 overflow-hidden bg-white">
                  <img src={v.img} alt={`Vaporisateur ${v.ml}`} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 text-center">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-2">Vaporisateur · {v.ml}</p>
                  <p className="text-4xl font-light text-[#1E7A6A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                    {v.price}€
                  </p>
                  <p className="text-sm text-[#332724] font-light leading-relaxed mb-2 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {v.desc}
                  </p>
                  <p className="text-xs text-[#332724]/65 mb-3">Rechargeable · 5 coloris au choix (magenta, noir, argent, bleu, violet)</p>
                  <div className="bg-[#B8922A]/10 border border-[#B8922A]/40 rounded-lg px-3 py-2 mb-4 text-left">
                    <p className="text-[10px] text-[#5C2018] leading-snug">
                      <strong>💡 Choix de la couleur :</strong> précisez-la dans <em>« Notes »</em> au moment de la commande. Sans indication, la couleur sera attribuée aléatoirement parmi les disponibles.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        addToCart({ sku: v.sku, label: `Harmony · Vaporisateur ${v.ml}`, unit_price: v.price });
                        toast.success(`Vaporisateur ${v.ml} ajouté au panier`, {
                          action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
                        });
                      }}
                      className="btn-primary px-6 py-2.5 rounded-full text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5"
                      data-testid={`${v.testid}-add`}
                    >
                      <ShoppingBag size={12} /> Ajouter au panier
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {showSection('coffret') && (
      <section className="py-20 px-6 bg-[#FDFAF4]" id="coffret">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Gift className="mx-auto mb-4 text-[#B8922A]" size={36} />
            <h2 className="text-4xl md:text-5xl font-light text-[#5C2018] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              Le coffret cadeau
            </h2>
            <p className="text-sm uppercase tracking-[0.25em] text-[#B8922A] mb-6">
              Supplément · +5€
            </p>
            <p className="text-lg text-[#332724] font-light leading-relaxed max-w-2xl mx-auto">
              L&apos;écrin sur-mesure qui sublime votre parfum. En supplément de l&apos;achat
              d&apos;un Rituel, d&apos;une recharge ou d&apos;un vaporisateur, choisissez l&apos;option
              <strong> Coffret cadeau</strong> pour offrir une présentation à la hauteur du
              geste : papier de soie, ruban, étiquette personnalisée à la main.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-white rounded-3xl p-8 border border-[#B8922A]/30 shadow-[0_20px_60px_rgba(92, 32, 24,0.08)]">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="/assets/coffrets.jpg"
                alt="Coffret cadeau Maison Harmony — écrin sur-mesure"
                className="w-full h-72 object-cover"
              />
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#B8922A]/70 italic text-center mt-2">
                📸 Photo non contractuelle
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#1E7A6A] mb-3">
                Ce que comprend l&apos;écrin
              </p>
              <ul className="space-y-3 text-sm text-[#332724] font-light mb-8">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#B8922A] mt-0.5 flex-shrink-0" />
                  <span>Boîte cadeau Harmony aux couleurs bordeaux / doré</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#B8922A] mt-0.5 flex-shrink-0" />
                  <span>Papier de soie crème + ruban satiné</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#B8922A] mt-0.5 flex-shrink-0" />
                  <span>Étiquette personnalisée à la main (prénom du destinataire)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-[#B8922A] mt-0.5 flex-shrink-0" />
                  <span>Carte-message manuscrite sur demande</span>
                </li>
              </ul>
              <p className="text-5xl font-light text-[#5C2018] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                +5€
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    addToCart({ sku: 'option-coffret', label: 'Harmony · Coffret cadeau (supplément écrin)', unit_price: 5 });
                    toast.success('Coffret cadeau ajouté au panier', {
                      action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } },
                    });
                  }}
                  className="btn-primary px-7 py-3 rounded-full text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                  data-testid="coffret-add-btn"
                >
                  <ShoppingBag size={14} /> Ajouter le coffret · 5€
                </button>
              </div>
              <p className="text-[11px] text-[#332724]/60 italic mt-4 text-center">
                ⚠️ À ajouter en complément de votre achat principal (parfum, recharge, vaporisateur).
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-[#332724]/70 italic mt-8 max-w-2xl mx-auto">
            « Un cadeau ne devrait jamais ressembler à un autre. » Chaque coffret est composé
            à la main dans notre boutique du Polygone de Béziers.
          </p>
        </div>
      </section>
      )}
    </div>
  );
};

export default BoutiquePage;
